import React, { PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components';
import { Drawer } from 'vaul';
import HBText from '../HBText/HBText';
import DrawerOptionsListItem, { IDrawerOption } from './DrawerOptionsListItem';
import { Z_INDEX } from '../../styles/variables';
import { DrawerContent, DrawerOverlay } from './HBDrawerComponents';
import spacing from '../../styles/spacing';
import { applyOpacity } from '../../utils/color.utils';
import { vars } from '../../theme';
import { useDetectScroll } from '../../hooks/useDetectScroll';
import HBCloseButton from '../HBCloseButton/HBCloseButton';
import ActionButtons, {
    BasicActionButtonsProps
} from '../common/ActionButtons';

export interface IDrawer extends BasicActionButtonsProps {
    /** CSS class applied to the root element. */
    className?: string;
    /** Called when the drawer requests to close — triggered by swipe-to-close or backdrop click. */
    onClose?(): void;
    /** Controls whether the drawer is visible. */
    open: boolean;
    /** Renders a list of selectable options above the children content; each option auto-closes the drawer on click. */
    options?: IDrawerOption[];
    /** Custom portal target element; when provided, the drawer renders inside this element with relative positioning instead of fixed. */
    parent?: HTMLElement;
    /** Secondary line of text displayed below the title in the drawer header. */
    subTitle?: string;
    /** Heading text displayed in the sticky drawer header. */
    title?: string;
    /** Removes the default 16px top padding from the body content area; automatically true when `options` are provided. */
    removeTopPadding?: boolean;
    /** Primary call-to-action button pinned to the bottom of the drawer. Accepts `text`, `onClick`, and optional `loading`, `icon`, and `iconPosition`. */
    primaryAction?: BasicActionButtonsProps['primaryAction'];
    /** Secondary action button pinned to the bottom of the drawer alongside `primaryAction`. Accepts the same shape as `primaryAction`. */
    secondaryAction?: BasicActionButtonsProps['secondaryAction'];
}

const HBDrawer = ({
    children,
    className,
    onClose,
    open,
    options,
    parent,
    subTitle,
    title,
    primaryAction,
    secondaryAction,
    removeTopPadding = false
}: PropsWithChildren<IDrawer>) => {
    const [contentElement, setContentElement] = useState<HTMLDivElement>(null);

    const { isScrolled } = useDetectScroll({
        elementRef: contentElement,
        detectCondition: open
    });

    const hasOptions = !!options?.length;

    const shouldAddTopPadding = !removeTopPadding && !hasOptions;

    return (
        <Drawer.Root open={open} onClose={onClose}>
            <Drawer.Portal container={parent}>
                <StyledDrawerOverlay isPortaledToParent={!!parent} />

                <StyledDrawerContent
                    className={className}
                    onClose={onClose}
                    setContentRef={setContentElement}
                >
                    <HeaderContainer isScrolling={isScrolled}>
                        <Handle />

                        {title && (
                            <Header>
                                <HBText
                                    type="display24"
                                    color="text.primary"
                                    testId="hb-drawer--title"
                                >
                                    {title}
                                </HBText>
                                {subTitle && (
                                    <HBText
                                        type="body16"
                                        color="text.secondary"
                                        testId="hb-drawer--subtitle"
                                    >
                                        {subTitle}
                                    </HBText>
                                )}
                            </Header>
                        )}
                        <HBCloseButton
                            distance={6}
                            size="sm"
                            onClick={onClose}
                            testIdName="drawer-close"
                            theme="dark"
                        />
                    </HeaderContainer>

                    <Body addTopPadding={shouldAddTopPadding}>
                        {options?.map(option => (
                            <DrawerOptionsListItem
                                closeDrawer={onClose}
                                key={option.testIdName}
                                {...option}
                            />
                        ))}
                        {children}
                    </Body>
                    <ActionButtons
                        sticky="bottom"
                        align="center"
                        primaryAction={primaryAction}
                        secondaryAction={secondaryAction}
                        containerRef={contentElement}
                    />
                </StyledDrawerContent>
            </Drawer.Portal>
        </Drawer.Root>
    );
};

export default HBDrawer;

// -------------------------------- Styles -------------------------------- //
const StyledDrawerOverlay = styled(DrawerOverlay)<{
    isPortaledToParent: boolean;
}>`
    position: fixed;
    inset: 0;
    z-index: ${Z_INDEX.TOP_MOST};
    background-color: ${applyOpacity(
        vars.colors.surface.containerInverted,
        70
    )};
    ${({ isPortaledToParent }) =>
        isPortaledToParent &&
        css`
            position: absolute;
            z-index: 2;
        `}
`;

const StyledDrawerContent = styled(DrawerContent)`
    position: fixed;
    z-index: ${Z_INDEX.POPOVER};
    bottom: 0;
    inset-inline: 0;
    max-height: 90%;
    overflow-y: auto;
    background: ${vars.colors.surface.container};
    border-top-left-radius: ${spacing[8]};
    border-top-right-radius: ${spacing[8]};
    &::after {
        display: none;
    }
`;

const inlinePadding = '16px';

const Handle = styled.div`
    height: 4px;
    border-radius: ${vars.radius.full};
    width: 40px;
    background: ${vars.colors.surface.track};
    position: relative;
    top: 2px;
    align-self: center;
`;

const HeaderContainer = styled.div<{ isScrolling: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-height: 48px;
    padding: 6px ${inlinePadding};
    z-index: 1;
    position: sticky;
    top: 0;
    background: ${vars.colors.surface.container};
    ${({ isScrolling }) =>
        isScrolling &&
        css`
            box-shadow: ${vars.shadow['10']};
        `}
`;

const Header = styled.div`
    align-self: flex-start;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
`;

const Body = styled.div<{ addTopPadding: boolean }>`
    padding: 0 ${inlinePadding} 20px;
    overflow-y: auto;
    ${({ addTopPadding }) =>
        addTopPadding &&
        css`
            padding-top: 16px;
        `}
`;
